import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  Plus, 
  Search, 
  Wrench, 
  ArrowLeft,
  MoreVertical,
  Edit,
  Trash2,
  Car,
  DollarSign,
  Camera,
  MessageCircle,
  Eye
} from 'lucide-react';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusOptions = [
  { value: 'reception', label: 'Recepción', color: 'bg-blue-100 text-blue-700' },
  { value: 'in_progress', label: 'En Progreso', color: 'bg-amber-100 text-amber-700' },
  { value: 'completed', label: 'Completado', color: 'bg-emerald-100 text-emerald-700' },
  { value: 'delivered', label: 'Entregado', color: 'bg-slate-100 text-slate-700' },
];

export default function ServicesPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const vehicleIdFilter = searchParams.get('vehicle_id');
  
  const [services, setServices] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [formData, setFormData] = useState({
    vehicle_id: vehicleIdFilter || '',
    description: '',
    status: 'reception',
    income: 0,
    costs: 0,
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, [vehicleIdFilter]);

  const fetchData = async () => {
    try {
      // Fetch services
      const servicesUrl = vehicleIdFilter 
        ? `${API_URL}/api/services?vehicle_id=${vehicleIdFilter}`
        : `${API_URL}/api/services`;
      
      const servicesRes = await fetch(servicesUrl, { credentials: 'include' });
      if (servicesRes.ok) {
        setServices(await servicesRes.json());
      }

      // Fetch vehicles and clients
      const [vehiclesRes, clientsRes] = await Promise.all([
        fetch(`${API_URL}/api/vehicles`, { credentials: 'include' }),
        fetch(`${API_URL}/api/clients`, { credentials: 'include' })
      ]);

      if (vehiclesRes.ok) setVehicles(await vehiclesRes.json());
      if (clientsRes.ok) setClients(await clientsRes.json());
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;
    
    setSaving(true);
    try {
      const url = editingService 
        ? `${API_URL}/api/services/${editingService.service_id}`
        : `${API_URL}/api/services`;
      
      const response = await fetch(url, {
        method: editingService ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...formData,
          income: parseFloat(formData.income) || 0,
          costs: parseFloat(formData.costs) || 0
        })
      });

      // Parse the JSON response
      let data;
      try {
        data = await response.json();
      } catch {
        data = {};
      }
      
      if (!response.ok) {
        throw new Error(data.detail || 'Error al guardar');
      }

      toast.success(editingService ? 'Servicio actualizado' : 'Servicio creado');
      setShowForm(false);
      setEditingService(null);
      resetForm();
      fetchData();
      
      // Navigate to service detail if new
      if (!editingService) {
        navigate(`/services/${data.service_id}`);
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (serviceId) => {
    if (!confirm('¿Estás seguro de eliminar este servicio?')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/services/${serviceId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Error al eliminar');
      }

      toast.success('Servicio eliminado');
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleStatusChange = async (serviceId, newStatus) => {
    try {
      const response = await fetch(`${API_URL}/api/services/${serviceId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status: newStatus })
      });

      if (!response.ok) throw new Error('Error al actualizar');
      
      toast.success('Estado actualizado');
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const resetForm = () => {
    setFormData({
      vehicle_id: vehicleIdFilter || '',
      description: '',
      status: 'reception',
      income: 0,
      costs: 0,
      notes: ''
    });
  };

  const getVehicleInfo = (vehicleId) => {
    const vehicle = vehicles.find(v => v.vehicle_id === vehicleId);
    return vehicle ? `${vehicle.brand} ${vehicle.model} (${vehicle.plates})` : 'Vehículo';
  };

  const getClientName = (clientId) => {
    const client = clients.find(c => c.client_id === clientId);
    return client?.name || 'Cliente';
  };

  const filteredServices = services.filter(service => {
    const matchesSearch = service.description.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === 'all' || service.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusStyle = (status) => {
    const opt = statusOptions.find(s => s.value === status);
    return opt?.color || 'bg-slate-100 text-slate-700';
  };

  return (
    <div className="animate-fade-in max-w-full overflow-x-hidden" data-testid="services-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          {vehicleIdFilter && (
            <button 
              onClick={() => navigate('/services')}
              className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Todos los servicios
            </button>
          )}
          <h1 className="text-2xl font-bold text-slate-900">Servicios</h1>
          <p className="text-slate-500">{services.length} servicio{services.length !== 1 ? 's' : ''}</p>
        </div>
        <Button
          onClick={() => {
            if (vehicles.length === 0) {
              toast.error('Primero debes registrar un vehículo');
              navigate('/vehicles');
              return;
            }
            setShowForm(true);
          }}
          className="bg-[#1E3A5F] hover:bg-[#162e4d]"
          data-testid="add-service-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nuevo Servicio
        </Button>
      </div>

      {/* Search and Filter */}
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Buscar servicios..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
            data-testid="search-services"
          />
        </div>
        <div className="flex gap-2 overflow-x-auto pb-2 md:pb-0">
          <button
            onClick={() => setStatusFilter('all')}
            className={`px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
              statusFilter === 'all' ? 'bg-[#1E3A5F] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            Todos
          </button>
          {statusOptions.map((status) => (
            <button
              key={status.value}
              onClick={() => setStatusFilter(status.value)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                statusFilter === status.value ? status.color : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {status.label}
            </button>
          ))}
        </div>
      </div>

      {/* Services List */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-28 w-full rounded-lg" />
          ))}
        </div>
      ) : filteredServices.length > 0 ? (
        <div className="space-y-3">
          {filteredServices.map((service) => (
            <div
              key={service.service_id}
              className="card-base card-hover p-4 overflow-hidden"
              data-testid={`service-${service.service_id}`}
            >
              <div className="flex items-start gap-3 md:gap-4">
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                  <Wrench className="w-5 h-5 md:w-6 md:h-6 text-amber-600" />
                </div>
                
                <div className="flex-1 min-w-0 overflow-hidden">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-1">
                    <Link 
                      to={`/services/${service.service_id}`}
                      className="font-semibold text-slate-900 hover:text-[#1E3A5F] truncate"
                    >
                      {service.description}
                    </Link>
                    <Select
                      value={service.status}
                      onValueChange={(value) => handleStatusChange(service.service_id, value)}
                    >
                      <SelectTrigger className={`h-7 w-auto px-2 ${getStatusStyle(service.status)} border-0 flex-shrink-0`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {statusOptions.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="flex flex-wrap gap-x-3 gap-y-1 text-sm text-slate-500 mb-2">
                    <span className="flex items-center gap-1 truncate max-w-[150px] sm:max-w-none">
                      <Car className="w-3.5 h-3.5 flex-shrink-0" />
                      <span className="truncate">{getVehicleInfo(service.vehicle_id)}</span>
                    </span>
                    <span className="truncate">{getClientName(service.client_id)}</span>
                  </div>

                  <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs sm:text-sm">
                    <span className="text-emerald-600 font-medium whitespace-nowrap">
                      <DollarSign className="w-3 h-3 sm:w-3.5 sm:h-3.5 inline" />
                      {service.income?.toLocaleString('es-MX')}
                    </span>
                    <span className="text-red-600 font-medium whitespace-nowrap">
                      <DollarSign className="w-3 h-3 sm:w-3.5 sm:h-3.5 inline" />
                      {service.costs?.toLocaleString('es-MX')}
                    </span>
                    <span className={`font-bold whitespace-nowrap ${service.profit >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                      ${service.profit?.toLocaleString('es-MX')}
                    </span>
                  </div>
                </div>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="flex-shrink-0">
                      <MoreVertical className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => navigate(`/services/${service.service_id}`)}>
                      <Eye className="w-4 h-4 mr-2" />
                      Ver detalles
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => handleDelete(service.service_id)}
                      className="text-red-600"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Eliminar
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="empty-state">
          <Wrench className="empty-state-icon" />
          <p className="text-slate-500 mb-4">
            {search || statusFilter !== 'all' ? 'No se encontraron servicios' : 'No hay servicios registrados'}
          </p>
          {!search && statusFilter === 'all' && (
            <Button onClick={() => setShowForm(true)} className="bg-[#1E3A5F]">
              <Plus className="w-4 h-4 mr-2" />
              Crear primer servicio
            </Button>
          )}
        </div>
      )}

      {/* Service Form Modal */}
      <Dialog open={showForm} onOpenChange={(open) => {
        if (!open) {
          setShowForm(false);
          setEditingService(null);
          resetForm();
        }
      }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingService ? 'Editar Servicio' : 'Nuevo Servicio'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="vehicle">Vehículo *</Label>
              <Select
                value={formData.vehicle_id}
                onValueChange={(value) => setFormData({ ...formData, vehicle_id: value })}
                required
              >
                <SelectTrigger data-testid="service-vehicle-select">
                  <SelectValue placeholder="Seleccionar vehículo" />
                </SelectTrigger>
                <SelectContent>
                  {vehicles.map((v) => (
                    <SelectItem key={v.vehicle_id} value={v.vehicle_id}>
                      {v.brand} {v.model} ({v.plates}) - {getClientName(v.client_id)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label htmlFor="description">Descripción del servicio *</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Ej: Cambio de aceite y filtros, Afinación mayor..."
                required
                rows={3}
                data-testid="service-description-input"
              />
            </div>
            
            <div>
              <Label htmlFor="status">Estado</Label>
              <Select
                value={formData.status}
                onValueChange={(value) => setFormData({ ...formData, status: value })}
              >
                <SelectTrigger data-testid="service-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {statusOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="income">Ingreso ($)</Label>
                <Input
                  id="income"
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.income}
                  onChange={(e) => setFormData({ ...formData, income: e.target.value })}
                  data-testid="service-income-input"
                />
              </div>
              <div>
                <Label htmlFor="costs">Costos ($)</Label>
                <Input
                  id="costs"
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.costs}
                  onChange={(e) => setFormData({ ...formData, costs: e.target.value })}
                  data-testid="service-costs-input"
                />
              </div>
            </div>
            
            <div className="p-3 bg-slate-50 rounded-lg">
              <span className="text-sm text-slate-500">Utilidad estimada:</span>
              <span className={`ml-2 font-bold ${(formData.income - formData.costs) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                ${((formData.income || 0) - (formData.costs || 0)).toLocaleString('es-MX')}
              </span>
            </div>
            
            <div>
              <Label htmlFor="notes">Notas adicionales</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={2}
                data-testid="service-notes-input"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                Cancelar
              </Button>
              <Button 
                type="submit" 
                className="bg-[#1E3A5F]" 
                data-testid="save-service-btn"
                disabled={saving}
              >
                {saving ? 'Guardando...' : editingService ? 'Guardar Cambios' : 'Crear Servicio'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
